import { API_URL } from "@env"
import { useEffect, useRef, useState } from "react"
import { ActivityIndicator, Image, Linking, Pressable, ScrollView, StyleSheet, Text, TouchableOpacity, View, useWindowDimensions } from "react-native"
import AntDesign from 'react-native-vector-icons/AntDesign'
import { ProfilePicture } from "./ProfilePicture"

// Bounded wait so a hung connection can never spin the payer forever.
const FETCH_TIMEOUT_MS = 20000
// Post-checkout capture window: how long/often to ask the server whether the
// charge settled before telling the payer we couldn't confirm yet.
const CHECK_MAX_ATTEMPTS = 10
const CHECK_INTERVAL_MS = 4000

function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

function fetchWithTimeout(url, options) {
    if (typeof AbortController === "undefined") {
        return fetch(url, options)
    }
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
    return fetch(url, { ...options, signal: controller.signal }).finally(
        () => clearTimeout(timer)
    )
}

// Map a raw /charge error into calm, blame-free payer copy. Never surfaces
// internal jargon; whitelists a couple of known, meaningful cases.
function humanizeChargeError(status, detail) {
    var d = (detail || "").toString().toLowerCase()
    if (status === 403 || d.indexOf("approved") !== -1 || d.indexOf("account") !== -1) {
        return "This invoice isn’t ready to accept payments yet. Please contact the sender."
    }
    if (d.indexOf("already") !== -1 || d.indexOf("paid") !== -1 || d.indexOf("deleted") !== -1) {
        return "This invoice has already been paid or is no longer available."
    }
    return "We couldn’t start the payment. Please try again."
}

function BaseButton({ title, style, textStyle, viewStyle, isLoading, onPress, disabled }) {
    return (
        <TouchableOpacity onPress={onPress} style={style} disabled={disabled} activeOpacity={0.85}>
            <View style={[viewStyle, disabled ? { opacity: 0.5 } : null]}>
                {isLoading ? <ActivityIndicator size="small" color={"#cfcfcf"} /> :
                    <Text adjustsFontSizeToFit numberOfLines={1} style={textStyle}>{title}</Text>
                }
            </View>
        </TouchableOpacity>
    )
}

function Button({ title, onPress, style, textStyle, viewStyle, isLoading, disabled }) {
    return (
        <BaseButton style={style}
            viewStyle={[styles.primaryButton1, viewStyle]}
            textStyle={[{ fontFamily: 'PoppinsSemiBold', fontWeight: '600', color: 'white' }, textStyle]}
            title={title}
            onPress={onPress}
            isLoading={isLoading}
            disabled={disabled}
        />
    )
}

// The invoice `issuer` has two shapes depending on `issuer_type`:
//   individual -> { id, display_name, profile_picture }   (to_base_info)
//   firm       -> { id, name, name_ar, logo, ... }        (to_mobile_response)
// Resolve to one { kind, displayName, isProfilePicture, userId } so the rest of
// the UI never branches on the raw payload again.
function resolveIssuer(invoiceInfo) {
    var issuer = (invoiceInfo && invoiceInfo.issuer) || {}
    var type = invoiceInfo && invoiceInfo.issuer_type
    if (type === "firm") {
        return {
            kind: "firm",
            displayName: issuer.name || issuer.name_ar || "",
            isProfilePicture: issuer.logo || null,
            userId: issuer.id
        }
    }
    return {
        kind: "user",
        displayName: issuer.display_name || "",
        isProfilePicture: issuer.profile_picture || null,
        userId: issuer.id
    }
}

function ProfileSection({ invoiceInfo }) {
    var issuer = resolveIssuer(invoiceInfo)
    return (
        <View style={styles.profileSection}>
            <ProfilePicture
                kind={issuer.kind}
                isProfilePicture={issuer.isProfilePicture}
                userId={issuer.userId}
                name={issuer.displayName}
                style={{ width: 45, height: 45, borderRadius: 100 }}
                textStyle={{ fontSize: 18 }}
            />
            <View>
                <Text style={{ fontSize: 11, fontFamily: "PoppinsSemiBold", letterSpacing: 1, color: '#8a90a0' }}>INVOICE FROM</Text>
                <Text style={{ fontFamily: "PoppinsSemiBold", fontSize: 13, color: '#1f2430', marginTop: 2 }}>{issuer.displayName}</Text>
            </View>
        </View>
    )
}

function PaymentMethod({ id, selected, onSelect, children, loading }) {
    return (
        <Pressable
            onPress={() => {
                if (!loading) {
                    onSelect(id)
                }
            }}
            accessibilityRole="button"
            accessibilityLabel={id === "card" ? "Credit Card" : id}
        >
            <View style={[styles.paymentMethod, selected === id ? styles.paymentMethodSelected : null]}>
                {children}
            </View>
        </Pressable>
    )
}

function PaymentMethods({ selectedPaymentMethod, setSelectedPaymentMethod, loading }) {
    return (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ paddingVertical: 6 }} contentContainerStyle={{ gap: 10 }}>
            <PaymentMethod id="benefit" selected={selectedPaymentMethod} onSelect={setSelectedPaymentMethod} loading={loading}>
                <Image source={require('./assets/benefit.png')} style={{ width: 28, height: 40 }} />
            </PaymentMethod>
            <PaymentMethod id="applepay" selected={selectedPaymentMethod} onSelect={setSelectedPaymentMethod} loading={loading}>
                <Image source={require('./assets/apple_pay.png')} style={{ width: 62.64, height: 40 }} />
            </PaymentMethod>
            <PaymentMethod id="card" selected={selectedPaymentMethod} onSelect={setSelectedPaymentMethod} loading={loading}>
                <View style={{ alignItems: 'center', justifyContent: 'center' }}>
                    <AntDesign name="creditcard" size={25} />
                    <Text style={{ fontSize: 11, fontFamily: "PoppinsRegular", userSelect: 'none' }}>Credit Card</Text>
                </View>
            </PaymentMethod>
        </ScrollView>
    )
}

function Breakdown({ invoiceInfo }) {
    return (
        <View style={{ gap: 8 }}>
            <View style={{ justifyContent: 'space-between', flexDirection: 'row', alignItems: 'center' }}>
                <Text style={{ fontFamily: "PoppinsRegular", fontSize: 13, color: '#6b7280' }}>
                    Amount
                </Text>
                <Text style={{ fontFamily: "PoppinsMedium", fontSize: 13, color: '#1f2430' }}>
                    {invoiceInfo["amount"].toFixed(2)} BD
                </Text>
            </View>
            <View style={{ justifyContent: 'space-between', flexDirection: 'row', alignItems: 'center' }}>
                <Text style={{ fontFamily: "PoppinsRegular", fontSize: 13, color: '#6b7280' }}>
                    Service charge
                </Text>
                <Text style={{ fontFamily: "PoppinsMedium", fontSize: 13, color: '#1f2430' }}>
                    {invoiceInfo["fee"].toFixed(2)} BD
                </Text>
            </View>
            <View style={{ borderBottomWidth: 1, borderStyle: 'dashed', borderColor: '#cfd4dc', marginVertical: 4 }} />
            <View style={{ justifyContent: 'space-between', flexDirection: 'row', alignItems: 'center' }}>
                <Text style={{ fontFamily: "PoppinsSemiBold", fontSize: 14, color: '#1f2430' }}>
                    Total
                </Text>
                <Text style={{ fontFamily: "PoppinsSemiBold", fontSize: 16, color: '#1f2430' }}>
                    {(invoiceInfo["amount"] + invoiceInfo["fee"]).toFixed(2)} BD
                </Text>
            </View>
        </View>
    )
}

function PaymentCompleted({ invoiceInfo }) {
    return (
        <View style={{ marginVertical: 12, alignItems: 'center' }}>
            <Image source={require('./assets/correct.png')} style={{ width: 72, height: 72 }} />
            <Text style={{ marginTop: 12, fontFamily: "PoppinsSemiBold", fontSize: 15, color: '#16a34a', textAlign: 'center' }}>
                Your payment has been successfully completed.
            </Text>
            <View style={{ marginTop: 12, alignItems: 'center', gap: 3 }}>
                <Text style={{ fontFamily: "PoppinsRegular", fontSize: 12, color: '#6b7280', textAlign: 'center' }}>
                    Reference ID: {invoiceInfo["reference_id"]}
                </Text>
                <Text style={{ fontFamily: "PoppinsRegular", fontSize: 12, color: '#6b7280', textAlign: 'center' }}>
                    Paid on: {new Date(invoiceInfo["completed_on"] * 1000).toLocaleString()}
                </Text>
                <Text style={{ fontFamily: "PoppinsRegular", fontSize: 12, color: '#6b7280', textAlign: 'center' }}>
                    Support: help@consultmebh.com
                </Text>
            </View>
        </View>
    )
}

function PaymentPending({ invoiceInfo, previousAttemptFailed }) {
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("")
    const [loading, setLoading] = useState(false)
    const [chargeError, setChargeError] = useState(null)

    const getPayText = () => {
        if (selectedPaymentMethod == "benefit") {
            return "Pay with Benefit"
        } else if (selectedPaymentMethod == "card") {
            return "Pay with Credit Card"
        } else if (selectedPaymentMethod == "applepay") {
            return "Pay with Apple Pay"
        } else {
            return "Pay Invoice"
        }
    }

    const startCharge = async () => {
        setLoading(true)
        setChargeError(null)
        try {
            const request = await fetchWithTimeout(`${API_URL}/invoice/${invoiceInfo["id"]}/charge`, {
                method: "POST",
                body: JSON.stringify({
                    type: selectedPaymentMethod
                }),
                headers: {
                    "Content-Type": "application/json"
                }
            })
            if (request.ok) {
                const response = await request.json()
                Linking.openURL(response["url"], "_self")
                return
            }
            let message = "We couldn’t start the payment. Please try again."
            try {
                const body = await request.json()
                message = humanizeChargeError(request.status, body && body.detail)
            } catch (_) {
                message = humanizeChargeError(request.status, null)
            }
            setChargeError(message)
        } catch (e) {
            // Network/timeout/abort. We don't know whether a charge was created
            // server-side, so do NOT imply success or failure — just retry.
            setChargeError("We couldn’t reach the payment service. Check your connection and try again.")
        } finally {
            setLoading(false)
        }
    }

    const payDisabled = !selectedPaymentMethod || loading

    return (
        <View>
            {previousAttemptFailed ? (
                <View style={styles.notice}>
                    <Text style={styles.noticeText}>
                        Your previous payment didn’t go through. You weren’t charged — please try again.
                    </Text>
                </View>
            ) : null}
            <View style={styles.section}>
                <Text style={styles.header}>Note</Text>
                <Text style={{ fontFamily: "PoppinsRegular", color: '#6b7280', fontSize: 13, lineHeight: 20 }}>{invoiceInfo["note"] ? invoiceInfo["note"] : "No note provided."}</Text>
            </View>
            <View style={styles.section}>
                <Text style={styles.header}>Payment Method</Text>
                <PaymentMethods
                    selectedPaymentMethod={selectedPaymentMethod}
                    setSelectedPaymentMethod={setSelectedPaymentMethod}
                    loading={loading}
                />
            </View>
            <View style={{ marginTop: 16, paddingBottom: 14 }}>
                <Text style={styles.header}>Breakdown</Text>
                <Breakdown invoiceInfo={invoiceInfo} />
            </View>
            <View style={{ marginTop: 20 }}>
                {selectedPaymentMethod == "applepay" ? (
                    <Button onPress={startCharge} title={getPayText()} disabled={payDisabled} isLoading={loading} viewStyle={{ backgroundColor: 'black', height: 50 }} />
                ) : (
                    <Button onPress={startCharge} title={getPayText()} disabled={payDisabled} isLoading={loading} viewStyle={{ height: 50 }} />
                )}
                {chargeError ? (
                    <Text style={styles.chargeErrorText}>{chargeError}</Text>
                ) : null}
                <Text style={styles.serviceAgreementText}>
                    By using this payment service, you agree to our <Text style={{ color: 'rgb(0, 54, 246)' }} onPress={() => Linking.openURL("/payment-agreement.pdf", "_blank")}>Payment Service Agreement</Text>
                </Text>
            </View>
        </View>
    )
}

// Strict finite-number check — never coerces. `Number(null)` is 0 and
// `Number("12")` is 12, both of which would silently paper over malformed
// stored money, so this rejects anything that isn't already typeof "number".
function isFiniteNumber(value) {
    return typeof value === "number" && Number.isFinite(value)
}

// True only when amount/fee are safe to render and pay against. A malformed
// invoice (null/missing/NaN/non-numeric amount or fee) must never reach
// Breakdown/PaymentPending — showing a wrong or blank total is worse than
// showing nothing.
function hasValidMoney(invoiceInfo) {
    return isFiniteNumber(invoiceInfo["amount"]) && invoiceInfo["amount"] > 0
        && isFiniteNumber(invoiceInfo["fee"]) && invoiceInfo["fee"] >= 0
}

// Reusable centered state card for not-found / load-error / processing /
// could-not-confirm. Keeps the same radius + shadow vocabulary as the invoice
// card so error states read as part of the design, not bolted on.
function InfoState({ icon, title, message, actionLabel, onAction }) {
    return (
        <View style={styles.container}>
            <View style={[styles.invoiceSection, { borderWidth: 0, padding: 28, alignItems: 'center', width: '90%', maxWidth: 360 }]}>
                {icon ? <View style={{ marginBottom: 12 }}>{icon}</View> : null}
                <Text style={{ fontFamily: 'PoppinsSemiBold', fontSize: 15, color: '#1f2430', textAlign: 'center' }}>{title}</Text>
                {message ? (
                    <Text style={{ marginTop: 8, fontFamily: 'PoppinsRegular', fontSize: 13, lineHeight: 20, color: '#6b7280', textAlign: 'center' }}>{message}</Text>
                ) : null}
                {actionLabel ? (
                    <TouchableOpacity onPress={onAction} style={styles.retryButton} activeOpacity={0.85} accessibilityRole="button" accessibilityLabel={actionLabel}>
                        <Text style={styles.retryText}>{actionLabel}</Text>
                    </TouchableOpacity>
                ) : null}
            </View>
        </View>
    )
}

export function Invoice({ navigation, route }) {
    const { width } = useWindowDimensions()
    const [invoiceInfo, setInvoiceInfo] = useState(null)
    // loadPhase: 'loading' | 'ready' | 'not_found' | 'load_error'
    const [loadPhase, setLoadPhase] = useState("loading")
    // confirming: null | 'processing' | 'check_error' | 'payment_failed'
    //   Only set on the ?completed=1 post-checkout path.
    const [confirming, setConfirming] = useState(null)

    const mountedRef = useRef(true)
    useEffect(() => {
        mountedRef.current = true
        return () => { mountedRef.current = false }
    }, [])

    // GET invoice by link. Returns a structured result so callers can branch on
    // not-found vs network without re-throwing.
    const getInvoiceInfo = async () => {
        try {
            const r = await fetchWithTimeout(
                `${API_URL}/invoice/get-invoice-by-link?link=${encodeURIComponent(route.params.invoiceLink)}`
            )
            if (r.ok) {
                return { ok: true, data: await r.json() }
            }
            if (r.status === 404) {
                return { ok: false, kind: "not_found" }
            }
            return { ok: false, kind: "network" }
        } catch (e) {
            return { ok: false, kind: "network" }
        }
    }

    // POST /invoice/check once. 200 => the (possibly just-completed) invoice.
    // non-2xx (capture window not yet captured, or terminal failure) / network
    // => ok:false; the caller decides whether to keep polling.
    const checkCharge = async (chargeId) => {
        try {
            const r = await fetchWithTimeout(`${API_URL}/invoice/check`, {
                method: "POST",
                body: JSON.stringify({
                    charge_id: chargeId
                }),
                headers: {
                    "Content-Type": "application/json"
                }
            })
            if (r.ok) {
                return { ok: true, data: await r.json() }
            }
            return { ok: false, status: r.status }
        } catch (e) {
            return { ok: false, status: 0 }
        }
    }

    const loadInvoice = async () => {
        setLoadPhase("loading")
        const res = await getInvoiceInfo()
        if (!mountedRef.current) return
        if (res.ok) {
            setInvoiceInfo(res.data)
            setLoadPhase("ready")
        } else if (res.kind === "not_found") {
            setLoadPhase("not_found")
        } else {
            setLoadPhase("load_error")
        }
    }

    // Post-checkout: confirm the charge with the server, polling through TAP's
    // capture window. Settles to completed-success, a definitive "didn't go
    // through" (invoice reopened to pending), or a neutral "couldn't confirm"
    // (still capturing, or we couldn't reach the server). Never implies a
    // payment failed when we don't actually know.
    const runCheckoutConfirm = async (chargeId) => {
        for (let attempt = 0; attempt < CHECK_MAX_ATTEMPTS; attempt++) {
            if (!mountedRef.current) return
            const res = await checkCharge(chargeId)
            if (!mountedRef.current) return
            if (res.ok && res.data && res.data.status === "completed") {
                setInvoiceInfo(res.data)
                setConfirming(null)
                setLoadPhase("ready")
                return
            }
            if (attempt < CHECK_MAX_ATTEMPTS - 1) {
                await delay(CHECK_INTERVAL_MS)
            }
        }
        if (!mountedRef.current) return
        // One status read to distinguish a reopened (definitively failed)
        // invoice from one still in the capture window / unreachable.
        const info = await getInvoiceInfo()
        if (!mountedRef.current) return
        if (info.ok) {
            if (info.data.status === "completed") {
                setInvoiceInfo(info.data)
                setConfirming(null)
                setLoadPhase("ready")
                return
            }
            if (info.data.status === "pending") {
                // Backend reopened the invoice after a terminal charge state —
                // the payment definitively did not go through.
                setInvoiceInfo(info.data)
                setConfirming("payment_failed")
                setLoadPhase("ready")
                return
            }
        }
        // status === 'charging' (still capturing) or we couldn't reach the
        // server: neutral, retryable, no failure implied.
        setConfirming("check_error")
    }

    useEffect(() => {
        if (route.params.completed == "1") {
            setConfirming("processing")
            runCheckoutConfirm(route.params.tap_id)
        } else {
            loadInvoice()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const retryLoad = () => loadInvoice()
    const retryConfirm = () => {
        setConfirming("processing")
        runCheckoutConfirm(route.params.tap_id)
    }

    if (loadPhase === "not_found") {
        return (
            <InfoState
                title="No invoice found."
                message="This link may be expired or incorrect. If you need help, contact help@consultmebh.com."
            />
        )
    }

    if (loadPhase === "load_error") {
        return (
            <InfoState
                icon={<AntDesign name="wifi" size={28} color="#8a90a0" />}
                title="Couldn’t load the invoice."
                message="Check your connection and try again."
                actionLabel="Try again"
                onAction={retryLoad}
            />
        )
    }

    if (confirming === "processing") {
        return (
            <InfoState
                icon={<ActivityIndicator size="large" color="rgb(0, 54, 246)" />}
                title="Confirming your payment."
                message="Give us a moment while we confirm with your bank."
            />
        )
    }

    if (confirming === "check_error") {
        return (
            <InfoState
                icon={<AntDesign name="search" size={28} color="#8a90a0" />}
                title="We couldn’t confirm your payment yet."
                message="This can take a moment. If it went through, you’ll receive a receipt by email. Check your connection and try again."
                actionLabel="Check again"
                onAction={retryConfirm}
            />
        )
    }

    if (loadPhase === "loading" || !invoiceInfo) {
        return (
            <View style={{ flex: 1, backgroundColor: '#f5f6f8', alignItems: 'center', justifyContent: 'center' }}>
                <ActivityIndicator size={"large"} color={"rgb(0, 54, 246)"} />
            </View>
        )
    }

    // PaymentCompleted never renders money, so only a pending/unpaid invoice
    // with malformed amount/fee needs to be blocked here. Never coerce,
    // recompute, or guess a value — an unavailable state is safer than a
    // wrong or blank one.
    if (invoiceInfo["status"] !== "completed" && !hasValidMoney(invoiceInfo)) {
        return (
            <InfoState
                title="This invoice is unavailable."
                message="Something looks wrong with this invoice. Please contact the sender."
            />
        )
    }

    return (
        <View style={styles.container}>
            <ScrollView style={{ flex: 1, width: '100%' }} contentContainerStyle={{ flexGrow: 1, alignItems: 'center', justifyContent: 'center', paddingVertical: 20 }}>
                <View style={[styles.invoiceSection, width > 450 ? { width: 450 } : { width: '95%' }]}>
                    <ProfileSection invoiceInfo={invoiceInfo} />
                    {invoiceInfo["status"] == "completed" ? (
                        <PaymentCompleted invoiceInfo={invoiceInfo} />
                    ) : (
                        <PaymentPending
                            invoiceInfo={invoiceInfo}
                            previousAttemptFailed={confirming === "payment_failed"}
                        />
                    )}
                </View>
            </ScrollView>
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#f5f6f8',
        alignItems: 'center',
        justifyContent: 'center'
    },
    invoiceSection: {
        padding: 22,
        backgroundColor: 'white',
        borderWidth: 1,
        borderColor: '#e6e8ec',
        borderRadius: 18,
        shadowColor: '#101828',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 16,
        elevation: 3
    },
    section: {
        marginTop: 16,
        paddingBottom: 14,
        borderBottomWidth: 1,
        borderBottomColor: '#eef0f3'
    },
    paymentMethod: {
        width: 95,
        height: 60,
        borderWidth: 1.5,
        borderRadius: 14,
        borderColor: '#e6e8ec',
        backgroundColor: '#ffffff',
        alignItems: 'center',
        justifyContent: 'center'
    },
    paymentMethodSelected: {
        borderColor: 'rgb(0, 54, 246)',
        backgroundColor: 'rgba(0, 54, 246, 0.05)'
    },
    primaryButton1: {
        backgroundColor: 'rgb(0, 54, 246)',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 14,
        width: '100%',
        shadowColor: '#101828',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.2,
        shadowRadius: 8,
        elevation: 2
    },
    header: {
        fontFamily: "PoppinsSemiBold",
        marginBottom: 10,
        fontSize: 14,
        color: '#1f2430'
    },
    serviceAgreementText: {
        fontFamily: "PoppinsRegular",
        color: '#6b7280',
        fontSize: 11,
        lineHeight: 15,
        textAlign: 'center',
        marginTop: 12
    },
    chargeErrorText: {
        fontFamily: "PoppinsRegular",
        color: '#b42318',
        fontSize: 12,
        lineHeight: 17,
        textAlign: 'center',
        marginTop: 12
    },
    notice: {
        backgroundColor: 'rgba(180, 35, 24, 0.06)',
        borderWidth: 1,
        borderColor: 'rgba(180, 35, 24, 0.18)',
        borderRadius: 12,
        paddingHorizontal: 14,
        paddingVertical: 10,
        marginBottom: 16
    },
    noticeText: {
        fontFamily: "PoppinsRegular",
        color: '#b42318',
        fontSize: 12.5,
        lineHeight: 18
    },
    retryButton: {
        marginTop: 18,
        paddingVertical: 12,
        paddingHorizontal: 24,
        borderRadius: 14,
        backgroundColor: 'rgb(0, 54, 246)',
        shadowColor: '#101828',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.2,
        shadowRadius: 8,
        elevation: 2
    },
    retryText: {
        fontFamily: "PoppinsSemiBold",
        fontWeight: '600',
        color: 'white',
        fontSize: 14
    },
    profileSection: {
        flexDirection: 'row',
        gap: 12,
        alignItems: 'center',
        borderBottomWidth: 1,
        paddingBottom: 14,
        borderBottomColor: '#eef0f3'
    }
})
